/**
 * Amplitude event name constants shared between client and server.
 *
 * - CLIENT events are intended to be fired from the browser (via `app/lib/amplitude`
 *   or `/api/analytics/track`), typically for CRUD actions and UI flows.
 * - SERVER events are intended to be fired from the backend (via `server/lib/amplitude`),
 *   typically for background jobs, enrichment, and match scoring.
 */

// CLIENT events only – used from app/lib/amplitude & app/hooks
export const CLIENT_AMPLITUDE_EVENTS = {
  // Auth
  LOGIN_SUCCESS: "Login Success",
  LOGOUT: "Logout",

  // CSV / operators (uploads and failures)
  OPERATOR_CSV_UPLOADED: "Operator CSV Uploaded",
  MATCHES_CSV_UPLOADED: "Matches CSV Uploaded",
  LEADS_CSV_UPLOADED: "Leads CSV Uploaded",

  OPERATOR_CSV_UPLOAD_FAILED: "Operator CSV Upload Failed",
  MATCHES_CSV_UPLOAD_FAILED: "Matches CSV Upload Failed",
  LEADS_CSV_UPLOAD_FAILED: "Leads CSV Upload Failed",

  // Brand (CRUD + views)
  BRAND_VIEWED: "Brand Viewed",
  BRAND_CREATED: "Brand Created",
  BRAND_UPDATED: "Brand Updated",
  BRAND_DELETED: "Brand Deleted",

  // Products
  PRODUCT_CREATED: "Product Created",
  PRODUCT_UPDATED: "Product Updated",
  PRODUCT_DELETED: "Product Deleted",
  PRODUCT_DELETE_FAILED: "Product Delete Failed",

  // Product groups
  PRODUCT_GROUP_CREATED: "Product Group Created",
  PRODUCT_GROUP_UPDATED: "Product Group Updated",
  PRODUCT_GROUP_DELETED: "Product Group Deleted",
  PRODUCT_GROUP_DELETE_FAILED: "Product Group Delete Failed",

  // Operators
  OPERATOR_NAME_UPDATED: "Operator Name Updated",

  // Clipboard copy actions
  EMAIL_COPIED: "Email Copied",
  PHONE_COPIED: "Phone Copied",
  // Email generation
  OPERATOR_EMAIL_GENERATED: "Operator Email Generated",

  // Lead uploads (My Uploads page)
  LEAD_UPLOADS_VIEW_MATCHES: "Lead Uploads View Matches",
  LEAD_UPLOADS_DOWNLOAD_ERRORS: "Lead Uploads Download Errors",

  // Exports
  MATCHES_EXPORTED: "Matches Exported",

  // Page views / operator & match UX
  MATCH_LIST_VIEWED: "Match List Viewed",
  LEADS_LIST_VIEWED: "Leads List Viewed",
  OPERATOR_VIEWED: "Operator Viewed",
  MATCH_VIEWED: "Match Viewed",
  LEAD_VIEWED: "Lead Viewed",
} as const;

// SERVER events only – used from server/lib/amplitude & workers (BullMQ/Redis jobs)
export const SERVER_AMPLITUDE_EVENTS = {
  // Brand match jobs
  BRAND_MATCHES_GENERATED: "Brand Matches Generated",
  BRAND_MATCH_GENERATION_FAILED: "Brand Match Generation Failed",
  BRAND_MATCH_JOB_STATUS_FAILED: "Brand Match Job Status Failed",
  BRAND_MATCH_JOB_CANCEL_FAILED: "Brand Match Job Cancel Failed",

  // Enrichment orchestration
  RE_ENRICH_TRIGGERED: "Re-Enrich Triggered",
  ENRICHMENT_JOB_COMPLETED: "Enrichment Job Completed",
  ENRICHMENT_JOB_FAILED: "Enrichment Job Failed",

  // Enrichment phase-level events (for debugging and observability)
  ENRICHMENT_VALIDATION_PHASE_STARTED: "Enrichment Validation Phase Started",
  ENRICHMENT_VALIDATION_PHASE_COMPLETED:
    "Enrichment Validation Phase Completed",
  ENRICHMENT_VALIDATION_PHASE_FAILED: "Enrichment Validation Phase Failed",
  ENRICHMENT_RESEARCH_PHASE_STARTED: "Enrichment Research Phase Started",
  ENRICHMENT_RESEARCH_PHASE_COMPLETED: "Enrichment Research Phase Completed",
  ENRICHMENT_RESEARCH_PHASE_FAILED: "Enrichment Research Phase Failed",
  ENRICHMENT_NEWS_PHASE_STARTED: "Enrichment News Phase Started",
  ENRICHMENT_NEWS_PHASE_COMPLETED: "Enrichment News Phase Completed",
  ENRICHMENT_NEWS_PHASE_FAILED: "Enrichment News Phase Failed",
  ENRICHMENT_NEWS_PHASE_SKIPPED: "Enrichment News Phase Skipped",
  ENRICHMENT_LOCATION_PHASE_STARTED: "Enrichment Location Phase Started",
  ENRICHMENT_LOCATION_PHASE_COMPLETED: "Enrichment Location Phase Completed",
  ENRICHMENT_LOCATION_PHASE_FAILED: "Enrichment Location Phase Failed",
  ENRICHMENT_MENU_PHASE_STARTED: "Enrichment Menu Phase Started",
  ENRICHMENT_MENU_PHASE_COMPLETED: "Enrichment Menu Phase Completed",
  ENRICHMENT_MENU_PHASE_FAILED: "Enrichment Menu Phase Failed",

  // Match scoring (API + worker, tied to operator/match data)
  MATCH_SCORING_TRIGGERED: "Match Scoring Triggered",
  MATCH_SCORING_QUEUED: "Match Scoring Queued",
  MATCH_SCORING_QUEUE_FAILED: "Match Scoring Queue Failed",
  MATCH_SCORING_BATCH_QUEUED: "Match Scoring Batch Queued",
  MATCH_SCORING_BATCH_QUEUE_FAILED: "Match Scoring Batch Queue Failed",
  MATCH_SCORING_STAGE_COMPLETED: "Match Scoring Stage Completed",
  MATCH_SCORING_COMPLETED: "Match Scoring Completed",
  MATCH_SCORING_FAILED: "Match Scoring Failed",
} as const;

// Combined view for backwards compatibility (avoid for new code).
export const AMPLITUDE_EVENTS = {
  ...CLIENT_AMPLITUDE_EVENTS,
  ...SERVER_AMPLITUDE_EVENTS,
} as const;

export type ClientAmplitudeEventName =
  (typeof CLIENT_AMPLITUDE_EVENTS)[keyof typeof CLIENT_AMPLITUDE_EVENTS];
export type ServerAmplitudeEventName =
  (typeof SERVER_AMPLITUDE_EVENTS)[keyof typeof SERVER_AMPLITUDE_EVENTS];
export type AmplitudeEventName =
  (typeof AMPLITUDE_EVENTS)[keyof typeof AMPLITUDE_EVENTS];
